import { useEffect, useRef, useState } from "react";
import { Network, NetworkProgram } from "../../API";
import { QueryStatus } from "../../app/types";
import {
  createNetworkProgram,
  createNetwork as createNetworkAPI,
  deleteNetworkProgram,
  fetchAllNetworks,
  fetchNetwork,
  fetchNetworkProgramsByPrograms,
  updateNetwork as updateNetworkAPI,
  NetworkWriteInput,
  fetchProgramIdentifierConfig,
  ProgramIdentifierConfig,
  fetchIdentifierTypes,
  IdentifierType
} from "./networksAPI";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { store } from "../../app/store";
import {
  selectNetworks,
  selectNetworksError,
  selectNetworksStatus,
  setNetworks,
  setNetworksError,
  setNetworksStatus
} from "./networksSlice";
import {
  selectNetworkProgramsByProgramIds,
  selectNetworkProgramsStatus,
  selectNetworkProgramsError,
  setNetworkPrograms,
  setNetworkProgramsStatus,
  setNetworkProgramsError
} from "./networkProgramsSlice";

export const useAllNetworks = () => {
  const allNetworks = useAppSelector(selectNetworks);
  const status = useAppSelector(selectNetworksStatus);
  const error = useAppSelector(selectNetworksError);

  const dispatch = useAppDispatch();

  const getAllNetworks = async () => {
    // If already loaded or currently loading, don't reload
    if (status === "loading" || status === "succeeded") {
      return;
    }

    dispatch(setNetworksStatus("loading"));
    dispatch(setNetworksError(undefined));

    try {
      const results = await fetchAllNetworks();
      dispatch(setNetworks(results));
      dispatch(setNetworksStatus("succeeded"));
    } catch (error) {
      console.error("error fetching all networks:", error);
      dispatch(setNetworksStatus("failed"));
      dispatch(setNetworksError(`Error loading networks: ${error}`));
    }
  };

  // No auto-load on mount - networks are loaded in auth flow for SysAdmins
  // Components that need networks should rely on Redux state or call getAllNetworks() explicitly

  return {
    networks: allNetworks,
    status,
    error,
    getAllNetworks
  };
};

// Module-level promise cache to prevent duplicate in-flight requests
// This is temporary until the request completes and Redux state is updated
const inFlightPromises = new Map<string, Promise<NetworkProgram[]>>();

export const useNetworkProgramsByPrograms = () => {
  const dispatch = useAppDispatch();
  const [lastRequestedProgramIds, setLastRequestedProgramIds] = useState<string[]>([]);

  const getNetworkProgramsByPrograms = async (programIds: string[]) => {
    const sortedIds = [...programIds].sort().join(",");
    
    // Get current Redux state for these programIds
    const state = store.getState();
    const currentStatus = selectNetworkProgramsStatus(state, programIds);
    const currentNetworkPrograms = selectNetworkProgramsByProgramIds(state, programIds);

    // Check if already loaded in Redux
    if (currentStatus === "succeeded" && currentNetworkPrograms.length > 0) {
      setLastRequestedProgramIds(programIds);
      return;
    }

    // Check if already loading (in-flight promise exists)
    const inFlightPromise = inFlightPromises.get(sortedIds);
    if (inFlightPromise) {
      setLastRequestedProgramIds(programIds);
      await inFlightPromise;
      return;
    }

    // Prevent duplicate calls with same programIds in this hook instance
    const lastSortedIds = [...lastRequestedProgramIds].sort().join(",");
    if (sortedIds === lastSortedIds && currentStatus === "loading") {
      return;
    }

    setLastRequestedProgramIds(programIds);
    dispatch(setNetworkProgramsStatus({ programIds, status: "loading" }));
    dispatch(setNetworkProgramsError({ programIds, error: undefined }));

    // Create promise and store it
    const apiPromise = fetchNetworkProgramsByPrograms(programIds).then(result => result || []);
    inFlightPromises.set(sortedIds, apiPromise);

    try {
      const results = await apiPromise;
      const finalResults = results || [];
      
      dispatch(setNetworkPrograms({ programIds, networkPrograms: finalResults }));
      dispatch(setNetworkProgramsStatus({ programIds, status: "succeeded" }));
    } catch (error) {
      console.error("error fetching networkprograms by program:", error);
      dispatch(setNetworkProgramsStatus({ programIds, status: "failed" }));
      dispatch(setNetworkProgramsError({ programIds, error: JSON.stringify(error) }));
    } finally {
      // Remove from in-flight cache once complete
      inFlightPromises.delete(sortedIds);
    }
  };

  // Get current programIds from last request, or use empty array
  const currentProgramIds = lastRequestedProgramIds.length > 0 
    ? lastRequestedProgramIds 
    : [];

  const networkPrograms = useAppSelector((state) =>
    selectNetworkProgramsByProgramIds(state, currentProgramIds)
  );
  const status = useAppSelector((state) =>
    selectNetworkProgramsStatus(state, currentProgramIds)
  );
  const error = useAppSelector((state) =>
    selectNetworkProgramsError(state, currentProgramIds)
  );

  return {
    networkPrograms,
    status,
    error,
    getNetworkProgramsByPrograms
  };
};

export const useNetworksByProgram = (programId?: string) => {
  const dispatch = useAppDispatch();
  const [networksAssociatedWithProgram, setNetworksAssociatedWithProgram] =
    useState<Network[]>([]);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>();

  const getNetworksByProgram = async (programId: string) => {
    setStatus("loading");
    setError(null);

    try {
      const programIds = [programId];
      const sortedIds = programIds.sort().join(",");
      
      // Check Redux state first
      const state = store.getState();
      const currentStatus = selectNetworkProgramsStatus(state, programIds);
      const currentNetworkPrograms = selectNetworkProgramsByProgramIds(state, programIds);
      
      let results: NetworkProgram[] | null = null;
      
      if (currentStatus === "succeeded" && currentNetworkPrograms.length > 0) {
        // Already in Redux - use it
        results = currentNetworkPrograms;
      } else {
        // Check if in-flight
        const inFlightPromise = inFlightPromises.get(sortedIds);
        if (inFlightPromise) {
          results = await inFlightPromise;
        } else {
          // Make API call via Redux
          dispatch(setNetworkProgramsStatus({ programIds, status: "loading" }));
          dispatch(setNetworkProgramsError({ programIds, error: undefined }));
          
          const apiPromise = fetchNetworkProgramsByPrograms(programIds).then(result => result || []);
          inFlightPromises.set(sortedIds, apiPromise);
          
          try {
            results = await apiPromise;
            dispatch(setNetworkPrograms({ programIds, networkPrograms: results }));
            dispatch(setNetworkProgramsStatus({ programIds, status: "succeeded" }));
          } catch (err) {
            dispatch(setNetworkProgramsStatus({ programIds, status: "failed" }));
            dispatch(setNetworkProgramsError({ programIds, error: JSON.stringify(err) }));
            throw err;
          } finally {
            inFlightPromises.delete(sortedIds);
          }
        }
      }
      const networks: Network[] = [];
      if (results) {
        for (const networkProgram of results) {
          networks.push(networkProgram.network);
        }
      }
      setNetworksAssociatedWithProgram(networks);
      setStatus("succeeded");
      return networks;
    } catch (error) {
      const errorString = `${error}`;
      console.error("error fetching networks by program:", error);
      setStatus("failed");
      setError(errorString);
    }
    return null;
  };

  useEffect(() => {
    if (programId) {
      getNetworksByProgram(programId);
    }
  }, [programId]);

  return {
    networksAssociatedWithProgram,
    status,
    error,
    getNetworksByProgram
  };
};

export const useNetworkById = (id?: string) => {
  const [network, setNetwork] = useState<Network | null>();
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>();

  const getNetwork = async (id: string): Promise<Network | null> => {
    setStatus("loading");
    setError(null);

    try {
      // View/edit pages don't render the program list — skip it for speed.
      const result = await fetchNetwork(id, false);
      setNetwork(result);
      setStatus("succeeded");
      return result;
    } catch (error) {
      console.error("error:", error);
      setStatus("failed");
      setError(`Error loading network: ${error}`);
    }
    return null;
  };

  useEffect(() => {
    if (id) {
      getNetwork(id);
    }
  }, [id]);

  return {
    network,
    status,
    error,
    getNetwork
  };
};

/**
 * SE-490 — resolves the per-network identifier config for a program, grouped
 * by role, from the program-identifier-config endpoint. The create/edit/bulk
 * forms use this to know which identifier fields apply for the selected role
 * and whether each is required. Re-fetches when programId changes.
 */
export const useNetworkIdentifierConfig = (programId?: string) => {
  const [config, setConfig] = useState<ProgramIdentifierConfig | null>();
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>();
  // Only the latest request may set state, so a slow response for a previously
  // selected program can't overwrite the current program's config.
  const latestRequest = useRef(0);

  const getConfig = async (
    id: string
  ): Promise<ProgramIdentifierConfig | null> => {
    const request = ++latestRequest.current;
    setStatus("loading");
    setError(null);
    try {
      const result = await fetchProgramIdentifierConfig(id);
      if (request !== latestRequest.current) return null;
      setConfig(result);
      setStatus("succeeded");
      return result;
    } catch (err) {
      if (request !== latestRequest.current) return null;
      console.error("error loading identifier config:", err);
      setStatus("failed");
      setError(`Error loading identifier config: ${err}`);
    }
    return null;
  };

  useEffect(() => {
    if (programId) {
      getConfig(programId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  return {
    config,
    status,
    error,
    getConfig
  };
};

/**
 * SE-490 — resolves identifier config for MANY programs at once (a user's
 * memberships span multiple program+role pairs, but identifier VALUES are
 * user-level). Returns a { programId -> config } map; the caller merges the
 * applicable identifiers across the user's memberships. Re-fetches only when
 * the SET of programIds changes.
 */
export const useProgramsIdentifierConfig = (programIds: string[]) => {
  const [configs, setConfigs] = useState<
    Record<string, ProgramIdentifierConfig>
  >({});
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [attempt, setAttempt] = useState(0);

  const distinctKey = Array.from(new Set(programIds.filter(Boolean)))
    .sort()
    .join(",");

  useEffect(() => {
    const ids = distinctKey ? distinctKey.split(",") : [];
    if (ids.length === 0) {
      setConfigs({});
      setStatus("succeeded");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    Promise.all(
      ids.map((id) =>
        fetchProgramIdentifierConfig(id)
          .then((c) => [id, c] as const)
          .catch((err) => {
            console.error("identifier config fetch failed for", id, err);
            return [id, null] as const;
          })
      )
    ).then((pairs) => {
      if (cancelled) return;
      const map: Record<string, ProgramIdentifierConfig> = {};
      let anyFailed = false;
      for (const [id, c] of pairs) {
        if (c) map[id] = c;
        else anyFailed = true;
      }
      setConfigs(map);
      // Fail closed: if ANY program's config couldn't be fetched, report
      // "failed" (still exposing the partial map) rather than "succeeded".
      // Consumers gate required-ness and identifier-column visibility on
      // status === "succeeded", so a silent partial resolve must not read as
      // fully resolved — otherwise a required identifier could be hidden.
      setStatus(anyFailed ? "failed" : "succeeded");
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distinctKey, attempt]);

  return { configs, status, retry: () => setAttempt((n) => n + 1) };
};

/** SE-3118 — every identifier type in the system (GET /identifiers), with a retry. */
export const useIdentifierTypes = () => {
  const [types, setTypes] = useState<IdentifierType[]>([]);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    fetchIdentifierTypes()
      .then((result) => {
        if (cancelled) return;
        setTypes(result);
        setStatus("succeeded");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("error loading identifier types:", err);
        setStatus("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  return { types, status, retry: () => setAttempt((n) => n + 1) };
};

export const useCreateNetwork = () => {
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const createNetwork = async (input: NetworkWriteInput) => {
    setStatus("loading");
    setError(null);

    try {
      const result = await createNetworkAPI(input);
      setStatus("succeeded");
      return result;
    } catch (error) {
      const errorString = `${error}`;
      console.error(errorString);
      setStatus("failed");
      setError(errorString);
    }
    return null;
  };

  return {
    status,
    error,
    createNetwork
  };
};

export const useUpdateNetwork = () => {
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const updateNetwork = async (id: string, input: NetworkWriteInput) => {
    setStatus("loading");
    setError(null);

    try {
      const result = await updateNetworkAPI(id, input);
      setStatus("succeeded");
      return result;
    } catch (error) {
      const errorString = `${error}`;
      console.error(errorString);
      setStatus("failed");
      setError(errorString);
    }
    return null;
  };

  return {
    status,
    error,
    updateNetwork
  };
};

export const NetworkFormErrors = {
  EmptyNetworkName: "Please enter a value for the network name.",
  NoSponsor: "Please select a sponsor for this network."
};

export const NetworkNotifications = {
  NoNetworks: "No networks to display."
};

export const NetworkErrors = {
  FetchNetworkError:
    "Error fetching network. Please try again or contact your administrator.",
  NetworkNotFound: "Network not found!",
  CreateNetworkError:
    "There was an error creating this network. Please try again or contact your administrator.",
  UpdateNetworkError:
    "There was an error updating this network. Please try again or contact your administrator."
};

export const alphabetizeNetworks = (networks: Network[]) => {
  return [...networks].sort((a, b) => {
    const nameA = a.name?.toLowerCase() ?? "";
    const nameB = b.name?.toLowerCase() ?? "";
    return nameA.localeCompare(nameB);
  });
};

export const useUpdateProgramNetworks = () => {
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const updateProgramNetworks = async (
    programId: string,
    networkIds: string[]
  ) => {
    setStatus("loading");
    setError(null);
    try {
      const existingNetworkPrograms = await fetchNetworkProgramsByPrograms([
        programId
      ]);
      if (existingNetworkPrograms) {
        for (const existingNetworkProgram of existingNetworkPrograms) {
          await deleteNetworkProgram(existingNetworkProgram.id);
        }
      }
      if (networkIds) {
        for (const networkId of networkIds) {
          await createNetworkProgram(programId, networkId);
        }
      }
      setStatus("succeeded");
    } catch (error) {
      console.error("error updating program networks:", error);
      setStatus("failed");
      setError(`Error updating program networks: ${error}`);
    }
  };

  return {
    status,
    error,
    updateProgramNetworks
  };
};
